import { useEffect, type ReactNode } from 'react';
import { supabase } from '../lib/supabase.js';
import { setSentryUser } from '../lib/sentry.js';
import { identifyUser, resetAnalytics, track } from '../lib/analytics.js';
import { trackMetaRegistration } from '../lib/metaPixel.js';
import { useAuthStore, type Profile } from './authStore.js';

/**
 * Cross-subdomain "I am signed in" hint cookie.
 *
 * The landing site at `fluent.ge` runs on a different origin than this app
 * at `app.fluent.ge`, so localStorage / Supabase session aren't visible
 * over there. We set a tiny non-sensitive boolean cookie scoped to the
 * shared parent domain so the landing's nav can swap from "Sign in / Sign
 * up free" to a single "Open app" link for users with an active session.
 *
 * The cookie is JUST a hint — no token, no user id. Real auth still goes
 * through Supabase + RLS. Worst case this cookie goes stale → user clicks
 * "Open app" → lands on /login if there's no real session. No data risk.
 */
const SIGNED_IN_COOKIE = 'fluent_signed_in';
function setSignedInHint(active: boolean) {
  if (typeof document === 'undefined') return;
  // Only set the parent-domain cookie when actually deployed under fluent.ge —
  // on localhost / preview-deploys the implicit per-host cookie is fine.
  const onFluentGe = window.location.hostname.endsWith('.fluent.ge') ||
    window.location.hostname === 'fluent.ge';
  const domainAttr = onFluentGe ? '; domain=.fluent.ge' : '';
  if (active) {
    document.cookie = `${SIGNED_IN_COOKIE}=1; path=/${domainAttr}; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
  } else {
    document.cookie = `${SIGNED_IN_COOKIE}=; path=/${domainAttr}; max-age=0; SameSite=Lax`;
  }
}

/**
 * Wires Supabase auth events into the Zustand store + loads the user's
 * profile row whenever the session changes.
 *
 * Mount this high in the tree (above the router) so every screen reads
 * consistent auth state.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const setSession = useAuthStore((s) => s.setSession);
  const setProfile = useAuthStore((s) => s.setProfile);
  const setProfileLoading = useAuthStore((s) => s.setProfileLoading);

  useEffect(() => {
    let cancelled = false;

    // 1. Resolve the initial session from localStorage (PKCE flow).
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setSentryUser(data.session?.user.id ?? null);
      setSignedInHint(!!data.session);
      if (data.session) void loadProfile(data.session.user.id);
    });

    // 2. Subscribe to future auth changes — sign-in / sign-out / token refresh.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setSentryUser(session?.user.id ?? null);
      setSignedInHint(!!session);
      if (session) {
        identifyUser(session.user.id);
        if (event === 'SIGNED_IN') {
          track('signup_completed', { user_id: session.user.id });
          // Email sign-ups were already counted when the form was sent
          // (SignUp.tsx) — this first sign-in is usually in another browser.
          if (session.user.app_metadata?.provider !== 'email') {
            trackMetaRegistration(session.user.id, session.user.created_at);
          }
        }
        void loadProfile(session.user.id);
      } else {
        resetAnalytics();
        setProfile(null);
      }
    });

    async function loadProfile(userId: string) {
      setProfileLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error('profile load failed', error);
        setProfile(null);
      } else {
        setProfile(data as Profile | null);
      }
      setProfileLoading(false);
    }

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [setSession, setProfile, setProfileLoading]);

  return <>{children}</>;
}
